import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchLinkedInTempOrganizations, 
  connectLinkedInPages,
  type LinkedInTempOrgsResponse,
  type LinkedInOrganization 
} from '../services/linkedinService';

/**
 * Hook to fetch LinkedIn temporary organizations.
 * 
 * Returns:
 *   Query result with organizations data, loading state, and error
 */
export const useLinkedInTempOrganizations = () => {
  return useQuery<LinkedInTempOrgsResponse, Error>({
    queryKey: ['linkedin-temp-organizations'],
    queryFn: fetchLinkedInTempOrganizations,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to connect LinkedIn pages.
 * 
 * Returns:
 *   Mutation with connect function and loading state
 */
export const useConnectLinkedInPages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ selectedPageIds, socialAccountId }: { selectedPageIds: string[], socialAccountId: string }) => 
      connectLinkedInPages(selectedPageIds, socialAccountId),
    onSuccess: () => {
      // Invalidate relevant queries after successful connection
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-temp-organizations'] });
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (error) => {
      console.error('Failed to connect LinkedIn pages:', error);
    },
  });
}; 